// Buy-page workshop UI: a read-only timetable + the "pick your workshop"
// selector shown when a workshop tier (Day Pass / Single) is in the cart. Pure
// presentation over the pure helpers in lib/workshop-picks; the server
// re-validates every pick authoritatively at checkout.
import type { FestivalSession, FestivalTier } from "../lib/types";
import {
  buildWorkshopSlots,
  tierSlotNeed,
  setSlotChoice,
  autoPicksForAll,
  type WorkshopSlot,
} from "../lib/workshop-picks";

// ── Timetable (schedule) ─────────────────────────────────────────────────────
export function WorkshopTimetable({ sessions }: { sessions: FestivalSession[] }) {
  const rows = sessions.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  if (rows.length === 0) return null;
  return (
    <div className="space-y-2">
      {rows.map((s) => (
        <div key={s.id} className="flex gap-3 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface)] p-3">
          <div className="w-28 shrink-0 text-xs font-medium text-[var(--muted)]">{s.timeLabel || ""}</div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--foreground)]">
              {s.title}
              {s.kind === "WORKSHOP" && (
                <span className="ml-2 rounded-full bg-[var(--accent)]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--accent)]">
                  Workshop
                </span>
              )}
            </p>
            {s.subtitle && <p className="text-xs text-[var(--muted)]">{s.subtitle}</p>}
            {s.kind === "WORKSHOP" && s.remaining != null && (
              <p className="mt-0.5 text-[11px] text-[var(--muted)]">{s.soldOut ? "Sold out" : `${s.remaining} spaces left`}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Picker ───────────────────────────────────────────────────────────────────
function OptionRow({
  session,
  checked,
  disabled,
  type,
  onSelect,
}: {
  session: FestivalSession;
  checked: boolean;
  disabled: boolean;
  type: "radio" | "checkbox";
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 rounded-[var(--radius-sm)] border p-3 ${
        disabled ? "opacity-50" : "cursor-pointer hover:bg-[var(--surface-2)]"
      } ${checked ? "border-[var(--accent)]" : "border-[var(--border-default)]"} bg-[var(--surface)]`}
    >
      <input type={type} className="accent-[var(--accent)]" checked={checked} disabled={disabled} onChange={onSelect} />
      <span className="min-w-0">
        <span className="block text-sm text-[var(--foreground)]">
          {session.title}
          {session.room ? <span className="text-[var(--muted)]"> · {session.room}</span> : null}
        </span>
        <span className="block text-[11px] text-[var(--muted)]">
          {session.timeLabel || ""}
          {session.soldOut ? " · Sold out" : session.remaining != null ? ` · ${session.remaining} left` : ""}
        </span>
      </span>
    </label>
  );
}

function TierPicker({
  tier,
  slots,
  chosen,
  onChange,
}: {
  tier: FestivalTier;
  slots: WorkshopSlot[];
  chosen: string[];
  onChange: (next: string[]) => void;
}) {
  const need = tierSlotNeed(tier, slots.length);

  if (tier.workshopMode === "all") {
    return (
      <div className="space-y-4">
        {slots.map((slot) => {
          const picked = chosen.find((id) => slot.options.some((o) => o.id === id)) ?? null;
          if (slot.options.length === 1) {
            const only = slot.options[0];
            return (
              <div key={slot.slotId} className="rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface)] p-3 text-sm text-[var(--subtle)]">
                <span className="text-[var(--muted)]">{only.timeLabel ? `${only.timeLabel} · ` : ""}Included:</span>{" "}
                <span className="text-[var(--foreground)]">{only.title}</span>
              </div>
            );
          }
          return (
            <div key={slot.slotId} className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">{slot.timeLabel || "Choose one"}</p>
              {slot.options.map((o) => (
                <OptionRow
                  key={o.id}
                  session={o}
                  type="radio"
                  checked={picked === o.id}
                  disabled={o.soldOut}
                  onSelect={() => onChange(setSlotChoice(chosen, slots, o.id))}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // "pick", choose `need` workshop(s), each in a distinct slot.
  const flat = slots.flatMap((s) => s.options);
  const slotOf = (id: string) => slots.find((s) => s.options.some((o) => o.id === id))?.slotId ?? null;
  const toggle = (id: string) => {
    if (need === 1) {
      onChange([id]);
      return;
    }
    if (chosen.includes(id)) {
      onChange(chosen.filter((x) => x !== id));
      return;
    }
    // Enforce distinct slots + the quota cap.
    const sid = slotOf(id);
    const withoutSameSlot = chosen.filter((x) => slotOf(x) !== sid);
    const next = [...withoutSameSlot, id];
    onChange(next.slice(-need));
  };
  return (
    <div className="space-y-2">
      <p className="text-xs text-[var(--muted)]">
        {need === 1 ? "Pick one workshop." : `Pick ${need} workshops (one per time slot).`}
      </p>
      {flat.map((o) => (
        <OptionRow
          key={o.id}
          session={o}
          type={need === 1 ? "radio" : "checkbox"}
          checked={chosen.includes(o.id)}
          disabled={o.soldOut && !chosen.includes(o.id)}
          onSelect={() => toggle(o.id)}
        />
      ))}
    </div>
  );
}

export function WorkshopPicker({
  tiers,
  sessions,
  qty,
  picks,
  onChangeTier,
}: {
  tiers: FestivalTier[];
  sessions: FestivalSession[];
  qty: Record<string, number>;
  picks: Record<string, string[]>;
  onChangeTier: (tierId: string, next: string[]) => void;
}) {
  const slots = buildWorkshopSlots(sessions);
  if (slots.length === 0) return null;

  const active = tiers.filter(
    (t) => (t.workshopMode === "all" || t.workshopMode === "pick") && (qty[t.id] ?? 0) > 0,
  );
  // Only prompt when there's an actual choice to make: an "all" tier whose every
  // slot has a single option needs nothing from the buyer.
  const needsInput = active.filter((t) => !(t.workshopMode === "all" && autoPicksForAll(slots) !== null));
  if (needsInput.length === 0) return null;

  return (
    <div className="space-y-6">
      {needsInput.map((tier) => (
        <div key={tier.id} className="space-y-3">
          <p className="text-sm font-medium text-[var(--foreground)]">{tier.name}, choose your workshop</p>
          <TierPicker
            tier={tier}
            slots={slots}
            chosen={picks[tier.id] ?? []}
            onChange={(next) => onChangeTier(tier.id, next)}
          />
        </div>
      ))}
    </div>
  );
}
